import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Outlet } from 'react-router';

import { Navigate } from '@sorare/routing';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useEnableSetFeatures } from '@sorare/core/src/hooks/commonGame/useEnableSetFeatures';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader.ts';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader.ts';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { ClaimablePacksReveal } from '#src/components/commonGame/ClaimablePacksReveal/index.tsx';
import { RaffleRewards } from '#src/components/series/raffle/RaffleRewards/index.tsx';

import type {
  SeriesLayoutQuery,
  SeriesLayoutQueryVariables,
} from './__generated__/layout.graphql.ts';

const SERIES_LAYOUT_QUERY = gql`
  query SeriesLayoutQuery($sport: Sport!) {
    currentUser {
      slug
      myUnclaimedSetRaffleRewards(sport: $sport) {
        id
        ...RaffleRewards_anyRewardInterface
      }
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        ...RaffleRewards_cardSet
      }
    }
  }
  ${RaffleRewards.fragments.anyRewardInterface}
  ${RaffleRewards.fragments.cardSet}
` as TypedDocumentNode<SeriesLayoutQuery, SeriesLayoutQueryVariables>;

export const loader = queryLoader(SERIES_LAYOUT_QUERY, ({ sport }) => ({
  sport,
}));

const SeriesLayout = () => {
  const { generateSportContextPath } = useSportContext();
  const enableSetFeatures = useEnableSetFeatures();
  const { data } = useQueryLoader<SeriesLayoutQuery>();
  const { myUnclaimedSetRaffleRewards } = data?.currentUser || {};

  if (!enableSetFeatures) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_HOME)}
        replace
        keepSearchParams
      />
    );
  }
  return (
    <RequireAuth>
      {myUnclaimedSetRaffleRewards?.length ? (
        <RaffleRewards
          rewards={myUnclaimedSetRaffleRewards}
          cardSet={data?.cardSet?.currentSet}
        />
      ) : (
        <ClaimablePacksReveal />
      )}
      <Outlet />
    </RequireAuth>
  );
};

export default SeriesLayout;
